// @flow
import _debug from 'debug';
import toggleArray from '@justpark/helpers/src/toggleArray/toggleArray';
import walkingDistanceRequest from '@justpark/api/src/requests/getWalkingDistance';
import getRequestError from '@justpark/api/src/helpers/getRequestError';
import calculateDateDiff from '@justpark/helpers/src/calculateDateDiff/calculateDateDiff';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { makeActionCreator } from '../helpers/utils';

const debug = _debug('jp:reducers:search');

type LatLng = {
  lat: number,
  lng: number
};

export const distance = (
  { lat: lat1, lng: lng1 }: LatLng,
  { coordinates: { lat: lat2, lng: lng2 } }: { coordinates: LatLng }
) => {
  const deglen = 110.25;
  const x = lat1 - lat2;
  const y = (lng1 - lng2) * Math.cos(lat2);
  return deglen * Math.sqrt(x * x + y * y);
};

const MONTHLY_UPGRADE_MODAL_VISIBLE = 'MONTHLY_UPGRADE_MODAL_VISIBLE';
const MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE =
  'MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE';
const MODAL_DISMISSED = 'DISMISSED';

export type State = {|
  hoveredId: number,
  filterCheckboxes: Array<string>,
  currentPanPosition: { lat: number, lng: number } | null,
  monthlyUpgradeModal: null | 'DISMISSED' | 'VISIBLE',
  dismissedOccupancySearchKey: boolean,
  walkingTimes: { [number]: number },
  walkingTimesLoaded: boolean
|};

export const defaultCoords = {
  lat: 51.5072178,
  lng: -0.1275862
};

export const initialState: () => State = () => ({
  selectedId: 0,
  hoveredId: 0,
  filterCheckboxes: [],
  currentPanPosition: null,
  monthlyUpgradeModal: null,
  dismissedOccupancySearchKey: false,
  walkingTimes: {},
  walkingTimesLoaded: false
});

const HOVER_ITEM_ENTER = 'search/HOVER_ITEM_ENTER';
const HOVER_ITEM_EXIT = 'search/HOVER_ITEM_EXIT';
export const TOGGLE_FILTER_PANEL = 'search/TOGGLE_FILTER_PANEL';

const PANNED = 'search/PANNED';
const SET_MONTHLY_UPGRADE_MESSAGE = 'search/SET_MONTHLY_UPGRADE_MESSAGE';
const DISMISS_MONTHLY_UPGRADE_MESSAGE =
  'search/DISMISS_MONTHLY_UPGRADE_MESSAGE';
export const TOGGLE_FILTER_CHECKBOX = 'search/TOGGLE_FILTER_CHECKBOX';
export const CLEAR_FILTER_CHECKBOXES = 'search/CLEAR_FILTER_CHECKBOXES';
export const SET_FILTER_CHECKBOXES = 'search/SET_FILTER_CHECKBOXES';
export const SET_PRICE_FILTER = 'search/SET_PRICE_FILTER';
export const DISMISS_OCCUPANCY_SEARCH_KEY =
  'search/DISMISS_OCCUPANCY_SEARCH_KEY';

type Action =
  | {| type: typeof HOVER_ITEM_ENTER, id: number |}
  | {| type: typeof DISMISS_MONTHLY_UPGRADE_MESSAGE |}
  | {| type: typeof TOGGLE_FILTER_CHECKBOX |}
  | {| type: typeof CLEAR_FILTER_CHECKBOXES |}
  | {| type: typeof SET_FILTER_CHECKBOXES |}
  | {| type: typeof SET_PRICE_FILTER |}
  | {| type: typeof TOGGLE_FILTER_PANEL |}
  | {| type: typeof PANNED, payload: { lat: number, lng: number } |}
  | {| type: typeof DISMISS_OCCUPANCY_SEARCH_KEY |};

export const splitCoords = (coords) =>
  coords.split(',').map((v) => parseFloat(v));

const getMonthlyUpgradeModalState = (state: State, dates) => {
  const { arriving, leaving } = dates;

  if (leaving === null) {
    return state.monthlyUpgradeModal;
  }

  const dayDiff = calculateDateDiff(arriving, leaving);

  if (dayDiff < 25 && state.monthlyUpgradeModal !== MODAL_DISMISSED) {
    return null;
  }

  if (
    dayDiff >= 25 &&
    dayDiff < 90 &&
    state.monthlyUpgradeModal !== MODAL_DISMISSED
  ) {
    return MONTHLY_UPGRADE_MODAL_VISIBLE;
  }

  if (dayDiff >= 90) {
    return MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE;
  }

  return state.monthlyUpgradeModal;
};

export const loadWalkingDistance = createAsyncThunk(
  'search/loadWalkingDistance',
  async (
    { coords, listingIds },
    { rejectWithValue, extra: { jpApiClient } }
  ) => {
    try {
      const request = walkingDistanceRequest(coords, listingIds);
      const { data } = await jpApiClient(request);

      return data.data;
    } catch (e) {
      return rejectWithValue(getRequestError(e));
    }
  }
);

const sliceName = 'search';

const searchSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset: () => initialState(),
    setPriceFilter: (state, action) => {
      debug('setPriceFilter', action);
      const value = action.payload;

      const removeValues = ['cheapest', 'average'];
      const filterCheckboxes = [...state.filterCheckboxes].filter(
        (item) => !removeValues.includes(item)
      );

      if (value !== 'any') {
        filterCheckboxes.push(value);
      }

      return {
        ...state,
        filterCheckboxes
      };
    }
  },
  extraReducers: (builder) => {
    builder.addCase(PANNED, (state, action) => {
      debug(PANNED, action);
      const { payload } = action;
      return {
        ...state,
        currentPanPosition: {
          lat: payload.lat,
          lng: payload.lng
        }
      };
    });
    builder.addCase(HOVER_ITEM_ENTER, (state, action) => {
      debug(HOVER_ITEM_ENTER);
      const { id } = action;
      return {
        ...state,
        hoveredId: id
      };
    });
    builder.addCase(HOVER_ITEM_EXIT, (state) => {
      debug(HOVER_ITEM_EXIT);
      return {
        ...state,
        hoveredId: null
      };
    });
    builder.addCase(TOGGLE_FILTER_CHECKBOX, (state, action) => {
      debug(TOGGLE_FILTER_CHECKBOX, action);
      const value = action.payload;
      const filterCheckboxes = [...state.filterCheckboxes];
      const newFilters = toggleArray(filterCheckboxes, value);

      return {
        ...state,
        filterCheckboxes: newFilters
      };
    });
    builder.addCase(SET_MONTHLY_UPGRADE_MESSAGE, (state, action) => {
      debug(SET_MONTHLY_UPGRADE_MESSAGE);

      const { dates } = action;

      const monthlyUpgradeModal = getMonthlyUpgradeModalState(state, dates);

      return {
        ...state,
        monthlyUpgradeModal
      };
    });
    builder.addCase(DISMISS_MONTHLY_UPGRADE_MESSAGE, (state) => ({
      ...state,
      monthlyUpgradeModal: MODAL_DISMISSED
    }));
    builder.addCase(loadWalkingDistance.pending, (state) => {
      debug(loadWalkingDistance.pending);

      return {
        ...state,
        walkingTimesLoaded: false
      };
    });
    builder.addCase(loadWalkingDistance.rejected, (state) => {
      debug(loadWalkingDistance.rejected);

      return {
        ...state,
        walkingTimesLoaded: true
      };
    });
    builder.addCase(loadWalkingDistance.fulfilled, (state, action) => {
      debug(loadWalkingDistance.fulfilled, action);

      if (action.payload) {
        const walkingTimes = {};
        action.payload.forEach((item) => {
          walkingTimes[item.listingId] = item.time;
        });

        return {
          ...state,
          walkingTimes,
          walkingTimesLoaded: true
        };
      }

      return {
        ...state,
        walkingTimesLoaded: true
      };
    });
    builder.addCase(SET_FILTER_CHECKBOXES, (state, action) => {
      debug(SET_FILTER_CHECKBOXES, action);

      const filterCheckboxes = [...action.payload, ...state.filterCheckboxes];

      const noDuplicateFilters = filterCheckboxes.filter(
        (item, index) => filterCheckboxes.indexOf(item) === index
      );

      return {
        ...state,
        filterCheckboxes: noDuplicateFilters
      };
    });
    builder.addCase(CLEAR_FILTER_CHECKBOXES, (state) => {
      debug(CLEAR_FILTER_CHECKBOXES);

      return {
        ...state,
        filterCheckboxes: []
      };
    });
    builder.addCase(DISMISS_OCCUPANCY_SEARCH_KEY, (state) => {
      debug(DISMISS_OCCUPANCY_SEARCH_KEY);
      return {
        ...state,
        dismissedOccupancySearchKey: true
      };
    });
  }
});

export default searchSlice;

export const { reset, setPriceFilter } = searchSlice.actions;

export const setMonthlyUpgradeMessage = ({ dates }): Action => ({
  dates,
  type: SET_MONTHLY_UPGRADE_MESSAGE
});

export const dismissOccupancySearchKey = (): Action => ({
  type: DISMISS_OCCUPANCY_SEARCH_KEY
});

export const setPanned = (payload: { lat: number, lng: number }) => ({
  type: PANNED,
  payload
});

export const dismissMonthlyUpgradeMessage = (): Action => ({
  type: DISMISS_MONTHLY_UPGRADE_MESSAGE
});

export function onMouseOver(type: string, id: number) {
  return (dispatch: (*) => Promise<*>) => {
    dispatch({ type: HOVER_ITEM_ENTER, id });
  };
}

export function onMouseOut() {
  return (dispatch: (*) => Promise<*>) => {
    dispatch({ type: HOVER_ITEM_EXIT });
  };
}

export const toggleFilterCheckbox = makeActionCreator(
  TOGGLE_FILTER_CHECKBOX,
  'payload'
);
export const setFilterCheckboxes = makeActionCreator(
  SET_FILTER_CHECKBOXES,
  'payload'
);
export const clearFilterCheckbox = makeActionCreator(CLEAR_FILTER_CHECKBOXES);

export const isMonthlyUpgradeModalVisible = (state: { search: State }) =>
  state.search.monthlyUpgradeModal === MONTHLY_UPGRADE_MODAL_VISIBLE ||
  state.search.monthlyUpgradeModal === MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE;

export const selectIsMonthlyAutoUpgraded = (state: { search: State }) =>
  state.search.monthlyUpgradeModal === MONTHLY_AUTO_UPGRADED_MODAL_VISIBLE;

export const selectSearchHasDismissedOccupancyKey = (state) =>
  state.search.dismissedOccupancySearchKey;

export const selectSearchWalkingTimes = (state) => state.search?.walkingTimes;
export const selectSearchWalkingTimesLoaded = (state) =>
  state.search.walkingTimesLoaded;

export const selectSearchCurrentPanPosition = (state) =>
  state.search?.currentPanPosition;

export const selectSearchReviews = (state) => state.search.reviews;

export const selectSearchHoveredId = (state) => state.search.hoveredId;
